import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';

const SGT = 'Asia/Singapore';

export function formatCheckedAtDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

export function orderIsChecked(order: {
  checked_at: string | null;
  checked_by?: string | null;
}): boolean {
  if (order.checked_at) return true;
  const by = String(order.checked_by ?? '').trim();
  return Boolean(by) && by !== '—' && by !== '-';
}

/** Processed and not yet checked — eligible for Needs check filter / Checked? action. */
export function orderNeedsCheck(order: {
  status: string;
  checked_at: string | null;
  checked_by?: string | null;
}): boolean {
  return order.status === 'Processed' && !orderIsChecked(order);
}

export function canMarkOrderChecked(order: {
  status: string;
  checked_at: string | null;
  checked_by?: string | null;
}): boolean {
  return orderNeedsCheck(order);
}

export function canUnmarkOrderChecked(order: { checked_at: string | null }): boolean {
  return orderIsChecked(order);
}

/** Mark Checked only when status is Processed (no Uploading bypass). */
export async function markOrderChecked(orderId: string, checkedBy: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('orders')
    .update({
      checked_at: new Date().toISOString(),
      checked_by: checkedBy,
    })
    .eq('order_id', orderId)
    .eq('status', 'Processed')
    .select('order_id');

  if (error) throw new Error(formatSupabaseError(error));
  if (!data?.length) {
    throw new Error('Only Processed orders can be marked Checked. Finish NAV import first.');
  }
}

export async function unmarkOrderChecked(orderId: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('orders')
    .update({
      checked_at: null,
      checked_by: null,
    })
    .eq('order_id', orderId);

  if (error) throw new Error(formatSupabaseError(error));
}
